import React, { useEffect, useState } from 'react'
import { Box, Divider } from '@mui/material'

import { useAuth } from '../AuthContext/AuthProvider'
import { useApp } from '../AppContext/AppProvider'
import {
  postBranchLogEvent,
  BRANCH_LOG_EVENTS
} from '../../services/branch.service'

import EmailVerification from './EmailVerification.component'
import {
  EmailVerificationData,
  NewCodeButtonEvent,
  NextButtonEvent,
  ScreenOpenedEvent
} from './types'
import { ProgressStepper } from '../ProgressStepper/Progress.component'
import { TOTAL_STEPS } from '../ApplicationProgress/types'
import {
  sendTrackingEvent,
  toButtonEvent,
  toScreenEvent
} from '../../services/amplitude.service'

const EmailVerificationContainer: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isInvalid, setIsInvalid] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const { userCredentials, getCredentials } = useAuth()
  const { application } = useApp()

  useEffect(() => {
    if (userCredentials?.verifiedEmail === false && !isLoading) {
      sendTrackingEvent(toScreenEvent({ ...ScreenOpenedEvent }))
      handleEmailNewCode()
    }
  }, [])

  const handleNewCodeClick = (): Promise<void> => {
    sendTrackingEvent(
      toButtonEvent({
        ...NewCodeButtonEvent
      })
    )
    return handleEmailNewCode()
  }

  const handleEmailNewCode = (): Promise<void> => {
    setIsError(false)
    setIsInvalid(false)
    setIsLoading(true)

    return application
      .service('auth/credentials/confirmation')
      .create({})
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }

  const handleOnSubmitCode = (data: EmailVerificationData): Promise<void> => {
    setIsError(false)
    setIsInvalid(false)
    setIsLoading(true)
    sendTrackingEvent(toButtonEvent({ ...NextButtonEvent }))

    return application
      .service('auth/credentials/confirmation')
      .update(null, data)
      .then(() => console.log('success'))
      .then(() => postBranchLogEvent(BRANCH_LOG_EVENTS.APP_STARTED))
      .then(getCredentials)
      .catch((e: any) => {
        // TODO: Figure out the types of error and improve handling
        if (
          e.error !== 'Invalid verification code provided, please try again.'
        ) {
          setIsError(true)
        }
        setIsInvalid(true)
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <Box sx={{ p: 5 }}>
      <Box sx={{ mt: -4, mb: 2, pr: 4, pl: 4 }}>
        <ProgressStepper step={1} totalSteps={TOTAL_STEPS} />
      </Box>

      <Divider sx={{ mt: -2.5, mr: -5, ml: -5 }} variant="fullWidth" />

      <EmailVerification
        email={userCredentials?.username || 'email address'}
        emailNewCode={handleNewCodeClick}
        onSubmitCode={handleOnSubmitCode}
        isInvalid={isInvalid}
        isError={isError}
        isLoading={isLoading}
      />
    </Box>
  )
}

export default EmailVerificationContainer
